import React, { useState, useEffect } from "react";
import {
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axiosInstance from "../utils/axiosConfig";

const Slots = () => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [id, setId] = useState("");
    const [status, setStatus] = useState("");
    const [node, setNode] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [open, setOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [editData, setEditData] = useState({});
    const [nodes, setNodes] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newSlotsCount, setNewSlotsCount] = useState(300);

    const fetchSlots = async () => {
        try {
            const response = await axiosInstance.get("/admin/slots", {
                params: {
                    id,
                    search,
                    userSearch,
                    status,
                    node,
                    startDate: startDate ? startDate.toISOString() : "",
                    endDate: endDate ? endDate.toISOString() : "",
                    page,
                },
            });
            setSlots(response.data.slots);
            setTotalPages(response.data.pages);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchNodes = async () => {
        try {
            const response = await axiosInstance.get("/admin/all-nodes");
            setNodes(response.data);
        } catch (err) {
            setError(err);
        }
    };

    useEffect(() => {
        fetchSlots();
        fetchNodes();
    }, [id, search, userSearch, status, node, startDate, endDate, page]);

    const handleEditClick = (slot) => {
        setSelectedSlot(slot);
        setEditData(slot);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedSlot(null);
    };

    const handleSave = async () => {
        try {
            await axiosInstance.put(`/admin/slots/${selectedSlot._id}`, editData);
            fetchSlots();
            handleClose();
        } catch (err) {
            setError(err);
        }
    };

    const handleReinitialize = async (slotId) => {
        if (window.confirm('Are you sure you want to reinitialize this slot? This will reset it to its initial state.')) {
            try {
                await axiosInstance.post(`/admin/slots/${slotId}/reinitialize`);
                fetchSlots();
                setSnackbar({
                    open: true,
                    message: "Slot reinitialized successfully",
                    severity: "success"
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || "Error reinitializing slot",
                    severity: "error"
                });
            }
        }
    };

    const handleReinitializeAll = async () => {
        if (window.confirm('Are you sure you want to reinitialize all slots? This will delete all existing slots and create new ones.')) {
            try {
                await axiosInstance.post('/admin/slots/reinitialize');
                fetchSlots();
                setSnackbar({
                    open: true,
                    message: "All slots reinitialized successfully",
                    severity: "success"
                });
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || "Error reinitializing slots",
                    severity: "error"
                });
            }
        }
    };

    const handleAddSlots = async () => {
        try {
            await axiosInstance.post('/admin/slots/add', { count: newSlotsCount });
            fetchSlots();
            setOpenAddDialog(false);
            setSnackbar({
                open: true,
                message: `Added ${newSlotsCount} new slots successfully`,
                severity: "success"
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || "Error adding slots",
                severity: "error"
            });
        }
    };

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Slots Management
                </Typography>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleReinitializeAll}
                    sx={{ ml: 2 }}
                >
                    Reinitialize All Slots
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenAddDialog(true)}
                    sx={{ ml: 2 }}
                >
                    Add New Slots
                </Button>
            </Box>
            <div style={{ marginBottom: "20px" }}>
                <TextField
                    label="Search by ID"
                    value={id}
                    onChange={(e) => {
                        setPage(1);
                        setId(e.target.value);
                    }}
                    style={{ marginRight: "10px" }}
                />
                <TextField
                    label="Search by slot number"
                    value={search}
                    onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                    }}
                    style={{ marginRight: "10px" }}
                />
                <TextField
                    label="Search by user (email/telegram)"
                    value={userSearch}
                    onChange={(e) => {
                        setPage(1);
                        setUserSearch(e.target.value);
                    }}
                    style={{ marginRight: "10px" }}
                />
                <FormControl style={{ marginRight: "10px", minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={status}
                        onChange={(e) => {
                            setPage(1);
                            setStatus(e.target.value);
                        }}
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="reserved">Reserved</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                </FormControl>
                <FormControl style={{ marginRight: "10px", minWidth: 120 }}>
                    <InputLabel>Node</InputLabel>
                    <Select
                        value={node}
                        onChange={(e) => {
                            setPage(1);
                            setNode(e.target.value);
                        }}
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {nodes.map((node) => (
                            <MenuItem key={node._id} value={node._id}>
                                {node.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => {
                            setPage(1);
                            setStartDate(newValue);
                        }}
                        slotProps={{ textField: { style: { marginRight: "10px" } } }}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => {
                            setPage(1);
                            setEndDate(newValue);
                        }}
                        slotProps={{ textField: { style: { marginRight: "10px" } } }}
                    />
                </LocalizationProvider>
            </div>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Slot Number</TableCell>
                            <TableCell>Node</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Reserved By</TableCell>
                            <TableCell>Reserved At</TableCell>
                            <TableCell>Expires At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {slots.map((slot) => (
                            <TableRow key={slot._id}>
                                <TableCell>{slot._id}</TableCell>
                                <TableCell>{slot.slotNumber}</TableCell>
                                <TableCell>{slot.node?.name || "N/A"}</TableCell>
                                <TableCell>
                                    {slot.isPaid
                                        ? "Paid"
                                        : slot.isReserved
                                        ? "Reserved"
                                        : "Available"}
                                </TableCell>
                                <TableCell>
                                    {slot.reservedBy ? (
                                        <div>
                                            {slot.reservedBy.email ? (
                                                <div>
                                                    <strong>Email:</strong> {slot.reservedBy.email}
                                                </div>
                                            ) : slot.reservedBy.tg ? (
                                                <div>
                                                    <strong>Telegram:</strong> @{slot.reservedBy.tg}
                                                </div>
                                            ) : (
                                                "No contact info"
                                            )}
                                        </div>
                                    ) : (
                                        "N/A"
                                    )}
                                </TableCell>
                                <TableCell>
                                    {slot.reservedAt
                                        ? new Date(slot.reservedAt).toLocaleString()
                                        : "N/A"}
                                </TableCell>
                                <TableCell>
                                    {slot.expiresAt
                                        ? new Date(slot.expiresAt).toLocaleString()
                                        : "N/A"}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleEditClick(slot)}
                                        sx={{ mr: 1 }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => handleReinitialize(slot._id)}
                                    >
                                        Reinitialize
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                />
            </div>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Edit Slot</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth style={{ marginTop: "20px" }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={editData.isPaid ? "paid" : editData.isReserved ? "reserved" : "available"}
                            onChange={(e) => {
                                const value = e.target.value;
                                setEditData({
                                    ...editData,
                                    isPaid: value === "paid",
                                    isReserved: value === "reserved",
                                });
                            }}
                        >
                            <MenuItem value="available">Available</MenuItem>
                            <MenuItem value="reserved">Reserved</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
                <DialogTitle>Add New Slots</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Number of slots to add"
                        type="number"
                        fullWidth
                        value={newSlotsCount}
                        onChange={(e) => setNewSlotsCount(parseInt(e.target.value) || 0)}
                        inputProps={{ min: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddSlots} color="primary">
                        Add Slots
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Slots; 